import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { filterPhone, isValidEmail } from '../utils/inputValidation';
import { getPlaceholders } from '../utils/placeholders';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

/* ── CSS ─────────────────────────────────────────────────────────────── */
const STYLES = `
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  .av {
    --ink:        #1C1814;
    --ink2:       #6B6560;
    --ink3:       #A8A3A0;
    --bg:         #F7F3EE;
    --surface:    #FFFFFF;
    --border:     #EBE5DC;
    --accent:     #1A8A7A;
    --accent-dk:  #0F5F54;
    --gold:       #C9922A;
    --gold-lt:    #FDF4E3;
    --r:          20px;
    --r-sm:       14px;
    font-family: system-ui,-apple-system,'Segoe UI',Helvetica,sans-serif;
    background: var(--bg);
    min-height: 100svh;
    color: var(--ink);
  }
  @media (prefers-color-scheme: dark) {
    .av {
      --ink:#EDE8E2; --ink2:#8C8780; --ink3:#5A5550;
      --bg:#0E100F; --surface:#1A1F1E; --border:#2A302E;
      --accent:#22B5A4; --accent-dk:#40D4C4;
      --gold:#E0B96A; --gold-lt:#1C1A10;
    }
  }
  :root[data-theme="dark"]  .av { --ink:#EDE8E2;--ink2:#8C8780;--ink3:#5A5550;--bg:#0E100F;--surface:#1A1F1E;--border:#2A302E;--accent:#22B5A4;--accent-dk:#40D4C4;--gold:#E0B96A;--gold-lt:#1C1A10; }
  :root[data-theme="light"] .av { --ink:#1C1814;--ink2:#6B6560;--ink3:#A8A3A0;--bg:#F7F3EE;--surface:#FFFFFF;--border:#EBE5DC;--accent:#1A8A7A;--accent-dk:#0F5F54;--gold:#C9922A;--gold-lt:#FDF4E3; }

  .av button { cursor:pointer; border:none; background:none; font:inherit; color:inherit; }
  .av input, .av textarea { font:inherit; }

  /* ── Page structure ── */
  .av-page {
    display: flex; flex-direction: column; align-items: center;
    min-height: 100svh; padding: 0 0 60px;
  }

  /* ── Top strip ── */
  .av-top {
    width: 100%; background: var(--ink);
    padding: 20px 24px 28px;
    display: flex; flex-direction: column; align-items: center; gap: 10px;
    position: relative; overflow: hidden;
  }
  .av-top::after {
    content:''; position:absolute; bottom:-1px; left:0; right:0; height:24px;
    background: var(--bg); border-radius: 24px 24px 0 0;
  }
  .av-top-salon {
    display: flex; align-items: center; gap: 8px;
    padding: 6px 16px; border-radius: 100px;
    background: rgba(255,255,255,0.10);
    font-size: 13px; font-weight: 700; color: rgba(255,255,255,0.85);
    letter-spacing: 0.02em;
  }
  .av-top-salon-dot { width:6px;height:6px;border-radius:50%;background:var(--gold);flex-shrink:0; }
  .av-top-title {
    font-family: 'Garamond','Georgia',serif;
    font-size: 28px; font-weight: normal; color: #fff;
    text-align: center; text-wrap: balance; line-height: 1.2;
    padding-bottom: 8px;
  }
  .av-top-sub { font-size: 14px; color: rgba(255,255,255,.55); text-align: center; }

  /* ── Body ── */
  .av-body { width: 100%; max-width: 440px; padding: 24px 20px 0; display: flex; flex-direction: column; gap: 14px; }

  /* ── Stars ── */
  .av-stars-wrap {
    background: var(--surface); border-radius: var(--r);
    padding: 24px 20px 20px;
    box-shadow: 0 2px 16px rgba(28,24,20,.06);
    border: 1px solid var(--border);
  }
  .av-stars-label {
    text-align: center; font-size: 11px; font-weight: 800;
    letter-spacing: 0.12em; text-transform: uppercase; color: var(--ink3);
    margin-bottom: 18px;
  }
  .av-stars-row {
    display: flex; justify-content: center; gap: 12px; margin-bottom: 14px;
  }
  .av-star-btn {
    background: none; border: none; cursor: pointer;
    padding: 4px; border-radius: 50%;
    transition: transform .15s cubic-bezier(.34,1.56,.64,1); line-height: 1;
  }
  .av-star-btn:active { transform: scale(.85) !important; }
  .av-rating-label {
    text-align: center; min-height: 26px;
    display: flex; align-items: center; justify-content: center;
  }
  .av-rating-badge {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 4px 14px; border-radius: 100px;
    font-size: 13px; font-weight: 700; transition: all .2s;
  }

  /* ── Sections ── */
  .av-section {
    background: var(--surface); border-radius: var(--r);
    padding: 18px 20px;
    box-shadow: 0 2px 16px rgba(28,24,20,.06);
    border: 1px solid var(--border);
  }
  .av-section-label {
    font-size: 11px; font-weight: 800; letter-spacing: 0.12em;
    text-transform: uppercase; color: var(--ink3); margin-bottom: 10px;
  }
  .av-section-label span { text-transform: none; letter-spacing: 0; font-weight: 400; }
  .av-textarea {
    width: 100%; resize: vertical; min-height: 80px;
    border: 1.5px solid var(--border); border-radius: var(--r-sm);
    background: var(--bg); color: var(--ink);
    padding: 11px 14px; font-size: 15px; line-height: 1.5;
    outline: none; transition: border-color .15s;
  }
  .av-textarea:focus { border-color: var(--accent); }
  .av-textarea::placeholder { color: var(--ink3); }
  .av-input {
    width: 100%; border: 1.5px solid var(--border); border-radius: var(--r-sm);
    background: var(--bg); color: var(--ink);
    padding: 13px 14px; font-size: 16px;
    outline: none; transition: border-color .15s;
  }
  .av-input:focus { border-color: var(--accent); }
  .av-input::placeholder { color: var(--ink3); font-size: 14px; }
  .av-input-stack { display: flex; flex-direction: column; gap: 10px; }
  .av-input-sublabel {
    font-size: 11px; font-weight: 800; letter-spacing: 0.12em;
    text-transform: uppercase; color: var(--ink3);
    margin-top: 4px; margin-bottom: 6px;
  }
  .av-input-sublabel span { text-transform: none; letter-spacing: 0; font-weight: 400; }

  /* ── Submit ── */
  .av-submit {
    width: 100%; padding: 17px;
    background: var(--accent); color: #fff;
    border-radius: var(--r-sm); font-size: 15px; font-weight: 700;
    letter-spacing: 0.01em;
    box-shadow: 0 4px 20px rgba(26,138,122,.30);
    transition: all .18s;
  }
  .av-submit:hover:not(:disabled) { background: var(--accent-dk); transform: translateY(-1px); box-shadow: 0 6px 28px rgba(26,138,122,.45); }
  .av-submit:active:not(:disabled) { transform: translateY(0); }
  .av-submit:disabled { opacity: .35; cursor: not-allowed; }

  /* ── Done screen ── */
  .av-done-wrap {
    background: var(--surface); border-radius: var(--r);
    box-shadow: 0 2px 16px rgba(28,24,20,.06);
    border: 1px solid var(--border);
    overflow: hidden; text-align: center;
  }
  .av-done-bar { height: 5px; background: linear-gradient(90deg, var(--accent), var(--gold)); }
  .av-done-body { padding: 36px 24px 28px; }
  .av-done-medal {
    width: 80px; height: 80px; border-radius: 50%;
    background: linear-gradient(135deg, var(--accent) 0%, var(--gold) 100%);
    display: flex; align-items: center; justify-content: center;
    font-size: 36px; margin: 0 auto 20px;
    box-shadow: 0 8px 32px rgba(26,138,122,.3);
  }
  .av-done-title {
    font-family: 'Garamond','Georgia',serif;
    font-size: 26px; font-weight: normal; color: var(--ink);
    margin-bottom: 8px; text-wrap: balance;
  }
  .av-done-sub { font-size: 14px; color: var(--ink2); line-height: 1.6; }

  /* ── Error / Spinner ── */
  .av-center { min-height:100svh; display:flex; flex-direction:column; align-items:center; justify-content:center; padding:24px; gap:16px; }
  .av-spin { width:36px;height:36px;border:3px solid var(--border);border-top-color:var(--accent);border-radius:50%;animation:av-spin .7s linear infinite; }
  @keyframes av-spin { to { transform: rotate(360deg); } }
  .av-spin-label { font-size:13px; color:var(--ink3); }
  .av-err-card { background:var(--surface);border-radius:var(--r);padding:36px 28px;max-width:340px;width:100%;text-align:center;box-shadow:0 4px 24px rgba(28,24,20,.10);border:1px solid var(--border); }
  .av-err-icon  { font-size:40px; margin-bottom:14px; }
  .av-err-title { font-size:20px;font-weight:700;color:var(--ink);margin-bottom:8px; }
  .av-err-sub   { font-size:14px;color:var(--ink2);line-height:1.6; }

  .av-error-msg { font-size:13px; color:#C0392B; text-align:center; }

  @keyframes av-in { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:none} }
  .av-body { animation: av-in .25s ease both; }
`;

/* ── Données étoiles ─────────────────────────────────────────────────── */
const STAR_DATA = [
  { label: 'Décevant',         color: '#e74c3c', bg: '#fdecea' },
  { label: 'Peut mieux faire', color: '#e67e22', bg: '#fef0e6' },
  { label: 'Correct',          color: '#f1c40f', bg: '#fefce8' },
  { label: 'Très bien',        color: '#27ae60', bg: '#eafaf1' },
  { label: 'Excellent !',      color: '#C9922A', bg: '#FDF4E3' },
];

function StarSVG({ filled, color, size = 42 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" style={{ display:'block', filter: filled ? `drop-shadow(0 2px 6px ${color}55)` : 'none' }}>
      <polygon
        points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26"
        fill={filled ? color : 'none'}
        stroke={filled ? color : '#D1CBC3'}
        strokeWidth="1.5"
        strokeLinejoin="round"
        style={{ transition: 'all .2s ease' }}
      />
    </svg>
  );
}

function Stars({ value, onChange }) {
  const [hover, setHover] = useState(0);
  const display = hover || value;
  const data    = display > 0 ? STAR_DATA[display - 1] : null;

  return (
    <div className="av-stars-wrap">
      <div className="av-stars-label">Votre note</div>
      <div className="av-stars-row">
        {[1,2,3,4,5].map(n => (
          <button key={n} className="av-star-btn"
            style={{ transform: n <= display ? 'scale(1.12)' : 'scale(1)' }}
            onClick={() => onChange(n)}
            onMouseEnter={() => setHover(n)}
            onMouseLeave={() => setHover(0)}>
            <StarSVG filled={n <= display} color={data?.color || '#C9922A'} size={44} />
          </button>
        ))}
      </div>
      <div className="av-rating-label">
        {data ? (
          <span className="av-rating-badge" style={{ background: data.bg, color: data.color }}>
            {display === 5 ? '🏆' : display >= 4 ? '👍' : display >= 3 ? '👌' : display >= 2 ? '😐' : '😔'}
            {data.label}
          </span>
        ) : (
          <span style={{ fontSize:13, color:'var(--ink3)' }}>Appuyez sur une étoile</span>
        )}
      </div>
    </div>
  );
}

/* ── Page ────────────────────────────────────────────────────────────── */
export default function AvisSalonPage() {
  const { slug } = useParams();
  const [salonNom,        setSalonNom]        = useState('');
  const [salonDevise,     setSalonDevise]     = useState('XOF');
  const [loading,         setLoading]         = useState(true);
  const [error,           setError]           = useState('');
  const [formError,       setFormError]       = useState('');
  const [done,            setDone]            = useState(false);
  const [rating,          setRating]          = useState(0);
  const [comment,         setComment]         = useState('');
  const [clientNom,       setClientNom]       = useState('');
  const [clientEmail,     setClientEmail]     = useState('');
  const [clientTelephone, setClientTelephone] = useState('');
  const [submitting,      setSubmitting]      = useState(false);

  useEffect(() => {
    axios.get(`${API}/avis/salon/${slug}`)
      .then(r => { setSalonNom(r.data?.salonNom || ''); setSalonDevise(r.data?.salonDevise || 'XOF'); })
      .catch(() => setError('Ce salon est introuvable ou n\'accepte plus les avis.'))
      .finally(() => setLoading(false));
  }, [slug]);

  const submit = async () => {
    if (!rating || !isValidEmail(clientEmail)) return;
    setFormError('');
    setSubmitting(true);
    try {
      await axios.post(`${API}/avis/salon/${slug}`, {
        rating,
        comment,
        clientNom:       clientNom.trim(),
        clientEmail:     clientEmail.trim(),
        clientTelephone: clientTelephone.trim(),
      });
      setDone(true);
    } catch (err) {
      setFormError(err.response?.data?.message || 'Une erreur est survenue. Veuillez réessayer.');
    } finally { setSubmitting(false); }
  };

  return (
    <>
      <style>{STYLES}</style>
      <div className="av">

        {/* Spinner */}
        {loading && (
          <div className="av-center">
            <div className="av-spin" />
            <span className="av-spin-label">Chargement…</span>
          </div>
        )}

        {/* Erreur */}
        {!loading && error && (
          <div className="av-center">
            <div className="av-err-card">
              <div className="av-err-icon">🔗</div>
              <div className="av-err-title">Salon introuvable</div>
              <div className="av-err-sub">{error}</div>
            </div>
          </div>
        )}

        {/* Formulaire */}
        {!loading && !error && !done && (
          <div className="av-page">
            <div className="av-top">
              {salonNom && (
                <div className="av-top-salon">
                  <span className="av-top-salon-dot" />
                  {salonNom}
                </div>
              )}
              <div className="av-top-title">Comment s'est passé<br />votre passage ?</div>
              <div className="av-top-sub">Votre avis nous aide à toujours mieux vous accueillir.</div>
            </div>

            <div className="av-body">
              <Stars value={rating} onChange={setRating} />

              <div className="av-section">
                <div className="av-section-label">Commentaire <span>(optionnel)</span></div>
                <textarea className="av-textarea"
                  placeholder="Partagez votre expérience : ce que vous avez aimé, ou ce qu'on peut améliorer…"
                  value={comment} onChange={e => setComment(e.target.value)} rows={3} />
              </div>

              <div className="av-section">
                <div className="av-section-label">Votre prénom <span>(optionnel)</span></div>
                <input className="av-input" type="text"
                  placeholder={getPlaceholders(salonDevise).prenom}
                  value={clientNom}
                  onChange={e => setClientNom(e.target.value)}
                  maxLength={80} />
              </div>

              <div className="av-section">
                <div className="av-section-label">
                  Votre email <span style={{ color:'var(--accent)', fontWeight:700, textTransform:'none', letterSpacing:0 }}>*</span>
                </div>
                <div className="av-input-stack">
                  <input className="av-input" type="email"
                    placeholder="exemple@mail.com"
                    value={clientEmail}
                    onChange={e => setClientEmail(e.target.value)}
                    style={clientEmail && !isValidEmail(clientEmail) ? { borderColor:'#C0392B' } : {}} />
                  {clientEmail && !isValidEmail(clientEmail) && (
                    <p style={{ fontSize:11, color:'#C0392B', marginTop:-4, lineHeight:1.4 }}>
                      Adresse email invalide (ex : nom@domaine.com)
                    </p>
                  )}
                  <div className="av-input-sublabel">
                    Téléphone <span>(optionnel)</span>
                  </div>
                  <input className="av-input" type="tel"
                    placeholder={getPlaceholders(salonDevise).telephone}
                    value={clientTelephone}
                    onChange={e => setClientTelephone(filterPhone(e.target.value))} />
                </div>
                <p style={{ fontSize:12, color:'var(--ink3)', marginTop:10, lineHeight:1.5 }}>
                  Votre email nous permettra de vous répondre directement si vous laissez un commentaire.
                </p>
              </div>

              {formError && <p className="av-error-msg">{formError}</p>}

              <button className="av-submit"
                disabled={!rating || !isValidEmail(clientEmail) || submitting}
                onClick={submit}>
                {submitting ? 'Envoi en cours…' : 'Envoyer mon avis →'}
              </button>
            </div>
          </div>
        )}

        {/* Confirmation */}
        {!loading && !error && done && (
          <div className="av-page">
            <div className="av-top" style={{ paddingBottom: 36 }}>
              {salonNom && (
                <div className="av-top-salon">
                  <span className="av-top-salon-dot" />
                  {salonNom}
                </div>
              )}
            </div>
            <div className="av-body">
              <div className="av-done-wrap">
                <div className="av-done-bar" />
                <div className="av-done-body">
                  <div className="av-done-medal">🙏</div>
                  <div className="av-done-title">Merci pour votre avis !</div>
                  <div className="av-done-sub">
                    Votre retour nous aide à nous améliorer et à mieux vous accueillir à chaque visite.
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>
    </>
  );
}
